/*
  Pure date bucketing and aggregation for the executive dashboard. No React, no fetch:
  the tab fetches daily rows once and everything here re-slices them when the
  grouping (week / month / quarter / year) or the range changes.

  Dates are 'YYYY-MM-DD' strings everywhere; arithmetic runs in UTC so daylight
  saving never shifts a day. Bucket ends are exclusive.
*/

export const MODES = [
  { key: 'WEEK', label: 'Week' },
  { key: 'MONTH', label: 'Month' },
  { key: 'QUARTER', label: 'Quarter' },
  { key: 'YEAR', label: 'Year' }
];

export const RANGES = [
  { key: '5m', label: 'Last 5 months', start: (today) => addMonths(today, -5) },
  { key: '12m', label: 'Last 12 months', start: (today) => addMonths(today, -12) },
  { key: 'ytd', label: 'This year', start: (today) => `${today.substring(0, 4)}-01-01` },
  { key: '24m', label: 'Last 24 months', start: (today) => addMonths(today, -24) },
  { key: '5y', label: 'Last 5 years', start: (today) => addMonths(today, -60) }
];

const DAY = 86400000;
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (value) => String(value).padStart(2, '0');
export const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
export const toIso = (date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

// today in the browser's own calendar (toISOString would already be tomorrow late in the evening)
export function localToday(now = new Date()) {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export const addDays = (iso, days) => toIso(new Date(toDate(iso).getTime() + days * DAY));

export function addMonths(iso, months) {
  const date = toDate(iso);
  const day = date.getUTCDate();
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return toIso(target);
}

export const addYears = (iso, years) => addMonths(iso, years * 12);

export function startOfBucket(iso, mode) {
  const date = toDate(iso);
  if (mode === 'WEEK') return addDays(iso, -((date.getUTCDay() + 6) % 7)); // ISO weeks start on Monday
  if (mode === 'MONTH') return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-01`;
  if (mode === 'QUARTER') return `${date.getUTCFullYear()}-${pad(Math.floor(date.getUTCMonth() / 3) * 3 + 1)}-01`;
  return `${date.getUTCFullYear()}-01-01`;
}

export function nextBucketStart(startIso, mode) {
  if (mode === 'WEEK') return addDays(startIso, 7);
  if (mode === 'MONTH') return addMonths(startIso, 1);
  if (mode === 'QUARTER') return addMonths(startIso, 3);
  return addMonths(startIso, 12);
}

// ISO-8601 week number
export function isoWeek(iso) {
  const date = toDate(iso);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7) + 3); // the Thursday of this week decides the year
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
  return 1 + Math.round((date - firstThursday) / (7 * DAY));
}

export function bucketLabel(start, mode) {
  const date = toDate(start);
  const shortYear = String(date.getUTCFullYear()).substring(2);
  if (mode === 'WEEK') return `${date.getUTCDate()} ${MONTHS_SHORT[date.getUTCMonth()]}`;
  if (mode === 'MONTH') return `${MONTHS_SHORT[date.getUTCMonth()]} ${shortYear}`;
  if (mode === 'QUARTER') return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${shortYear}`;
  return String(date.getUTCFullYear());
}

// long form for tooltips and the table view; end is exclusive
export function bucketTitle(start, end, mode) {
  const date = toDate(start);
  const last = toDate(addDays(end, -1));
  if (mode === 'WEEK') return `Week ${isoWeek(start)} · ${date.getUTCDate()} ${MONTHS_SHORT[date.getUTCMonth()]} – ${last.getUTCDate()} ${MONTHS_SHORT[last.getUTCMonth()]} ${last.getUTCFullYear()}`;
  if (mode === 'MONTH') return `${MONTHS_LONG[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  if (mode === 'QUARTER') return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()} (${MONTHS_SHORT[date.getUTCMonth()]}–${MONTHS_SHORT[last.getUTCMonth()]})`;
  return String(date.getUTCFullYear());
}

// the comparable period a year earlier: same calendar period, or 52 weeks back for weeks (keeps weekdays aligned)
export function previousYearRange(start, end, mode) {
  if (mode === 'WEEK') return { start: addDays(start, -364), end: addDays(end, -364) };
  return { start: addYears(start, -1), end: addYears(end, -1) };
}

// buckets from the one containing rangeStart through the one containing today; the last one is usually partial
export function buildBuckets({ rangeStart, today, mode }) {
  const buckets = [];
  const dayAfterToday = addDays(today, 1);
  let start = startOfBucket(rangeStart, mode);
  while (start <= today) {
    const fullEnd = nextBucketStart(start, mode);
    const partial = fullEnd > dayAfterToday;
    const end = partial ? dayAfterToday : fullEnd;
    const previous = previousYearRange(start, end, mode);
    buckets.push({
      key: start,
      start,
      end,
      partial,
      label: bucketLabel(start, mode),
      title: bucketTitle(start, fullEnd, mode),
      prevStart: previous.start,
      prevEnd: previous.end
    });
    start = fullEnd;
  }
  return buckets;
}

// the earliest day the daily series must cover so every grouping and its prior-year comparison can be built without refetching
export function seriesFromDate(rangeStart) {
  return addYears(startOfBucket(rangeStart, 'YEAR'), -1);
}

//----------------------------------------------------------------------------
// daily rows -> buckets
//----------------------------------------------------------------------------
const numeric = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

// Map day -> { v, n } from rows { d, v, n }; minus rows are subtracted (credit notes) without touching the count
export function dailyMap(plusRows, minusRows = []) {
  const byDay = new Map();
  const entry = (day) => {
    if (!byDay.has(day)) byDay.set(day, { v: 0, n: 0 });
    return byDay.get(day);
  };
  for (const row of plusRows || []) {
    const item = entry(row.d);
    item.v += numeric(row.v);
    item.n += numeric(row.n);
  }
  for (const row of minusRows || []) entry(row.d).v -= numeric(row.v);
  return byDay;
}

export function sumRange(byDay, start, end) {
  let v = 0;
  let n = 0;
  for (let day = start; day < end; day = addDays(day, 1)) {
    const item = byDay.get(day);
    if (!item) continue;
    v += item.v;
    n += item.n;
  }
  return { v, n };
}

// one point per bucket: this period and the comparable period a year earlier
export function bucketSeries(buckets, byDay) {
  return buckets.map((bucket) => ({ ...bucket, current: sumRange(byDay, bucket.start, bucket.end), previous: sumRange(byDay, bucket.prevStart, bucket.prevEnd) }));
}

//----------------------------------------------------------------------------
// order backlog over time
//----------------------------------------------------------------------------
// Prepares the orders once: invoices grouped per order, plus the date of the last invoice ever.
export function prepareOrders(orders, invoices) {
  const byOrder = new Map();
  for (const invoice of invoices || []) {
    if (!byOrder.has(invoice.o)) byOrder.set(invoice.o, []);
    byOrder.get(invoice.o).push({ d: invoice.d, a: numeric(invoice.a) });
  }
  return (orders || []).map((order) => {
    const list = (byOrder.get(order.id) || []).sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : 0));
    return { id: order.id, d: order.d, v: numeric(order.v), closed: Boolean(order.closed), invoices: list, lastInvoiceDate: list.length ? list[list.length - 1].d : null };
  });
}

// Backlog on one day, the way the order backlog card defines it today: non-canceled orders of the
// last 24 months whose value (incl. VAT) is not fully invoiced yet. A closed order only counts until
// its last invoice (we cannot know when it was closed; a closed order without invoices never counts).
export function orderbookAt(preparedOrders, atIso) {
  const windowStart = addMonths(atIso, -24);
  let v = 0;
  let n = 0;
  for (const order of preparedOrders) {
    if (order.d > atIso || order.d < windowStart) continue;
    if (order.closed && (!order.lastInvoiceDate || atIso >= order.lastInvoiceDate)) continue;
    let invoiced = 0;
    for (const invoice of order.invoices) {
      if (invoice.d > atIso) break;
      invoiced += invoice.a;
    }
    const remaining = order.v - invoiced;
    if (remaining > 1) {
      v += remaining;
      n += 1;
    }
  }
  return { v, n };
}

// value at the end of every bucket (today for the running one) and a year earlier
export function orderbookSeries(buckets, preparedOrders, today) {
  return buckets.map((bucket) => {
    const at = bucket.end > today ? today : addDays(bucket.end, -1);
    const prevAt = addDays(bucket.prevEnd, -1);
    return { ...bucket, current: orderbookAt(preparedOrders, at), previous: orderbookAt(preparedOrders, prevAt), at, prevAt };
  });
}

//----------------------------------------------------------------------------
// small helpers for the tiles
//----------------------------------------------------------------------------
// the last `months` calendar months (running month included) as month buckets
export function recentMonthBuckets(today, months = 12) {
  return buildBuckets({ rangeStart: addMonths(startOfBucket(today, 'MONTH'), -(months - 1)), today, mode: 'MONTH' });
}

export function sumSeries(points, side = 'current') {
  return (points || []).reduce((total, point) => total + numeric(point[side]?.v), 0);
}
